import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconArrowLeft, IconCompass, IconTargetArrow, IconWand, IconChecklist, IconFolder,
  IconPlus, IconCheck, IconTrash, IconClipboardCheck, IconArchive,
  IconBuildingCommunity, IconUsersGroup, IconUpload, IconDownload,
  IconMailForward, IconAlertTriangle, IconEdit, IconX,
} from "@tabler/icons-react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal";

// Toda la UI del módulo Brújula usa trato de "Usted" (per user request)
// El backend expone /api/brujula/school para leer el branding institucional.

export default function BrujulaHome() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [school, setSchool] = useState(null);
  const [tab, setTab] = useState("overview");

  const schoolRole = user?.is_admin ? "director" : (user?.school_role || "director");
  const isDocente = schoolRole === "docente";
  const canEditBranding = schoolRole === "director" || schoolRole === "orientador";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/brujula/school");
        setSchool(data);
      } catch { /* */ }
    })();
  }, []);

  // Docentes: solo ven Adecuaciones
  const TABS = useMemo(() => {
    const all = [
      { id: "overview", es: "Panorama", en: "Overview", icon: <IconCompass size={13} /> },
      { id: "institucion", es: "Institución", en: "Institution", icon: <IconBuildingCommunity size={13} /> },
      { id: "equipo", es: "Equipo", en: "Staff", icon: <IconUsersGroup size={13} /> },
      { id: "indice", es: "Índice de inclusión", en: "Inclusion index", icon: <IconTargetArrow size={13} /> },
      { id: "simulacro", es: "Simulacro", en: "Mock inspection", icon: <IconClipboardCheck size={13} /> },
      { id: "adecuaciones", es: "Adecuaciones", en: "Accommodations", icon: <IconWand size={13} /> },
      { id: "trazabilidad", es: "Trazabilidad", en: "Tracking", icon: <IconChecklist size={13} /> },
      { id: "defensa", es: "Carpeta de defensa", en: "Defense folder", icon: <IconFolder size={13} /> },
    ];
    if (isDocente) return all.filter((t) => t.id === "adecuaciones");
    if (schoolRole === "orientador") return all.filter((t) => t.id !== "equipo");
    return all;
  }, [schoolRole, isDocente]);

  // Auto-select first available tab
  useEffect(() => {
    if (!TABS.find((t) => t.id === tab)) {
      setTab(TABS[0]?.id || "adecuaciones");
    }
  }, [TABS, tab]);

  const primaryColor = school?.primary_color || "#004F4F";

  // Institutional greeting header (uses LOGGED-IN user's name, not the school director's)
  const rawName = user?.name || "";
  const displayName = rawName.split(" ").slice(0, 2).join(" ");
  const startsWithTitle = /^(dr\.|dra\.|dir\.|dirª\.|lic\.|mtro\.|mtra\.|prof\.|ing\.|arq\.)/i.test(rawName);
  const genderTitle = startsWithTitle ? "" : (user?.gender === "femenino" ? "Dra." : user?.gender === "masculino" ? "Dr." : "");

  return (
    <>
      <div className="subbar">
        <button className="btn-ghost btn text-[12px] px-2 py-1" onClick={() => nav("/app")} data-testid="brujula-back">
          <IconArrowLeft size={13} /> {lang === "es" ? "Volver" : "Back"}
        </button>
        <span className="t">MÓDULO BRÚJULA</span>
      </div>
      <div className="wrap-app max-w-[1080px] py-10">
        {/* Institution header banner (branding) */}
        {school && (
          <div
            className="rounded-xl p-5 mb-6 flex items-center gap-4 flex-wrap"
            style={{ background: `linear-gradient(135deg, ${primaryColor}12 0%, ${primaryColor}05 100%)`, borderLeft: `4px solid ${primaryColor}` }}
            data-testid="brujula-inst-banner"
          >
            {school.logo_url && (
              <img
                src={school.logo_url}
                alt="logo"
                className="w-14 h-14 object-contain rounded-md bg-white p-1 border border-line"
                data-testid="brujula-inst-logo"
              />
            )}
            <div className="flex-1 min-w-[200px]">
              <p className="text-[11px] tracking-[0.14em] uppercase font-medium" style={{ color: primaryColor }}>
                {lang === "es" ? "Panel institucional" : "Institutional panel"}
              </p>
              <h1 className="font-display font-medium text-[clamp(22px,3vw,30px)] mt-0.5" style={{ color: primaryColor }}>
                {school.name}
              </h1>
              <p className="text-tert text-[12.5px] mt-0.5">
                {[school.address, school.city, school.country].filter(Boolean).join(" · ")}
              </p>
            </div>
            {!isDocente && (
              <div className="text-right hidden md:block">
                <p className="text-tert text-[11px] uppercase tracking-wider">{school.director_title || "Director"}</p>
                <p className="text-[13.5px] font-medium">{school.director_name}</p>
              </div>
            )}
          </div>
        )}

        <p className="text-muted text-[15px]">
          {lang === "es"
            ? `Le damos la bienvenida, ${genderTitle ? genderTitle + " " : ""}${displayName}.`
            : `Welcome, ${displayName}.`}
        </p>

        <div className="border-b border-line flex gap-1 overflow-x-auto mt-6">
          {TABS.map((tt) => (
            <button
              key={tt.id}
              onClick={() => setTab(tt.id)}
              className={`px-3 py-2 text-[12.5px] font-medium transition border-b-2 -mb-px flex items-center gap-1.5 whitespace-nowrap ${tab === tt.id ? "border-teal text-teal-deep" : "border-transparent text-muted hover:text-teal-deep"}`}
              data-testid={`brujula-tab-${tt.id}`}
            >
              {tt.icon} {lang === "es" ? tt.es : tt.en}
            </button>
          ))}
        </div>
        <div className="mt-6">
          {tab === "overview" && <Overview school={school} />}
          {tab === "institucion" && <InstitucionTab school={school} setSchool={setSchool} canEdit={canEditBranding} />}
          {tab === "equipo" && <EquipoTab />}
          {tab === "indice" && <IndiceInclusion />}
          {tab === "simulacro" && <Simulacro />}
          {tab === "adecuaciones" && <Adecuaciones schoolRole={schoolRole} />}
          {tab === "trazabilidad" && <Trazabilidad schoolRole={schoolRole} />}
          {tab === "defensa" && <Defensa />}
        </div>
      </div>
    </>
  );
}

// ============================================================
// TAB: Overview
// ============================================================
function Overview({ school }) {
  const { lang } = useI18n();
  const [d, setD] = useState(null);
  useEffect(() => { (async () => { try { const { data } = await api.get("/brujula/overview"); setD(data); } catch { /* */ } })(); }, []);
  if (!d) return <p className="text-tert text-[12px] font-mono uppercase">Cargando…</p>;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="kpi"><div className="kl">{lang === "es" ? "Índice inclusión" : "Inclusion index"}</div><div className="kv">{d.inclusion_index?.score ?? "—"}%</div>{d.inclusion_index?.band && <span className="tag mt-1 inline-block">{d.inclusion_index.band}</span>}</div>
        <div className="kpi"><div className="kl">{lang === "es" ? "Último simulacro" : "Last mock"}</div><div className="kv">{d.last_simulacro?.score ?? "—"}%</div></div>
        <div className="kpi"><div className="kl">{lang === "es" ? "Planes activos" : "Active plans"}</div><div className="kv">{d.active_plans}</div></div>
        <div className="kpi"><div className="kl">{lang === "es" ? "Docs defensa" : "Defense docs"}</div><div className="kv">{d.defense_docs}</div></div>
      </div>
      {d.last_simulacro?.weakest && (
        <div className="card border-l-[3px] border-l-honey">
          <p className="text-[13.5px]"><span className="lbl m-0 inline-block mr-2">{lang === "es" ? "Área débil:" : "Weakest:"}</span> {d.last_simulacro.weakest}</p>
        </div>
      )}
      {school && (school.mission || school.vision || school.values) && (
        <div className="card">
          <p className="eyebrow">{lang === "es" ? "Nuestra institución" : "Our institution"}</p>
          {school.mission && <p className="text-[13.5px] mt-2"><span className="lbl m-0 inline-block mr-2">Misión</span>{school.mission}</p>}
          {school.vision && <p className="text-[13.5px] mt-2"><span className="lbl m-0 inline-block mr-2">Visión</span>{school.vision}</p>}
          {school.values && <p className="text-[13.5px] mt-2"><span className="lbl m-0 inline-block mr-2">Valores</span>{school.values}</p>}
        </div>
      )}
    </div>
  );
}

// ============================================================
// TAB: Institución (branding)
// ============================================================
function InstitucionTab({ school, setSchool, canEdit }) {
  const { lang } = useI18n();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (school) setForm({
      name: school.name || "",
      address: school.address || "",
      city: school.city || "",
      country: school.country || "",
      director_name: school.director_name || "",
      director_title: school.director_title || "Director",
      orientador_title: school.orientador_title || "Orientador",
      primary_color: school.primary_color || "#004F4F",
      secondary_color: school.secondary_color || "#B08D57",
      mission: school.mission || "",
      vision: school.vision || "",
      values: school.values || "",
    });
  }, [school]);

  if (!form) return <p className="text-tert text-[12px]">Cargando…</p>;

  const save = async () => {
    setSaving(true); setMsg("");
    try {
      const { data } = await api.patch("/brujula/school", form);
      setSchool(data);
      setMsg(lang === "es" ? "Cambios guardados correctamente." : "Saved.");
    } catch (e) { setMsg(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSaving(true); setMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/brujula/school/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSchool({ ...school, logo_url: data.logo_url, logo_path: data.logo_path });
      setMsg(lang === "es" ? "Logotipo actualizado." : "Logo updated.");
    } catch (err) { setMsg(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <p className="text-muted text-[13.5px]">
        {lang === "es"
          ? "Personalice la identidad visual y la información de su institución. Estos datos aparecerán en los reportes descargables (Word) de todo el módulo."
          : "Customize your institution branding. This appears on all downloadable Word reports."}
      </p>

      {!canEdit && (
        <div className="card border-l-[3px] border-l-honey">
          <p className="text-[13px]">{lang === "es" ? "Solo el Director o la Orientadora pueden modificar estos datos." : "Only Director / Coordinator can edit."}</p>
        </div>
      )}

      {/* Logo uploader */}
      <div className="card">
        <p className="lbl m-0">{lang === "es" ? "Logotipo institucional" : "Institution logo"}</p>
        <div className="flex items-center gap-4 mt-3 flex-wrap">
          <div className="w-24 h-24 bg-white border border-line rounded-md flex items-center justify-center overflow-hidden" data-testid="school-logo-preview">
            {school?.logo_url ? (
              <img src={school.logo_url} alt="logo" className="max-w-full max-h-full object-contain" />
            ) : (
              <IconBuildingCommunity size={32} className="text-tert" />
            )}
          </div>
          {canEdit && (
            <label className="btn btn-ghost text-[12px] cursor-pointer" data-testid="school-logo-upload-btn">
              <IconUpload size={13} /> {lang === "es" ? "Subir logo (PNG/JPG · máx 3 MB)" : "Upload logo (PNG/JPG · 3 MB max)"}
              <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="school-logo-input" />
            </label>
          )}
        </div>
      </div>

      {/* Identidad */}
      <div className="card space-y-3">
        <p className="lbl m-0">{lang === "es" ? "Identidad" : "Identity"}</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="field"><label>{lang === "es" ? "Nombre de la institución" : "Name"}</label>
            <input className="input" value={form.name} disabled={!canEdit} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="school-name" /></div>
          <div className="field"><label>{lang === "es" ? "Dirección" : "Address"}</label>
            <input className="input" value={form.address} disabled={!canEdit} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="school-address" /></div>
          <div className="field"><label>{lang === "es" ? "Ciudad" : "City"}</label>
            <input className="input" value={form.city} disabled={!canEdit} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="school-city" /></div>
          <div className="field"><label>{lang === "es" ? "País" : "Country"}</label>
            <input className="input" value={form.country} disabled={!canEdit} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="school-country" /></div>
        </div>
      </div>

      {/* Cargos editables */}
      <div className="card space-y-3">
        <p className="lbl m-0">{lang === "es" ? "Cargos institucionales" : "Institutional titles"}</p>
        <p className="text-tert text-[12px]">{lang === "es" ? "Cada institución nombra estos cargos a su manera. Escriba cómo desea que aparezcan en la plataforma y en los reportes." : "Institutions name these roles differently. Set the labels shown in reports."}</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="field"><label>{lang === "es" ? "Nombre del/a Director/a" : "Director name"}</label>
            <input className="input" value={form.director_name} disabled={!canEdit} onChange={(e) => setForm({ ...form, director_name: e.target.value })} data-testid="school-dir-name" /></div>
          <div className="field"><label>{lang === "es" ? "Título del cargo principal" : "Head title"}</label>
            <input className="input" value={form.director_title} disabled={!canEdit} onChange={(e) => setForm({ ...form, director_title: e.target.value })} placeholder="Director / Rector / Directora General" data-testid="school-dir-title" /></div>
          <div className="field sm:col-span-2"><label>{lang === "es" ? "Título de Orientador/Coordinador" : "Coordinator title"}</label>
            <input className="input" value={form.orientador_title} disabled={!canEdit} onChange={(e) => setForm({ ...form, orientador_title: e.target.value })} placeholder="Orientador / Coordinador / Psicopedagogo" data-testid="school-ori-title" /></div>
        </div>
      </div>

      {/* Colores */}
      <div className="card space-y-3">
        <p className="lbl m-0">{lang === "es" ? "Colores institucionales" : "Institutional colors"}</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="field">
            <label>{lang === "es" ? "Color primario" : "Primary color"}</label>
            <div className="flex items-center gap-2">
              <input type="color" value={form.primary_color} disabled={!canEdit} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="w-14 h-10 border border-line rounded cursor-pointer" data-testid="school-color-primary" />
              <input className="input" value={form.primary_color} disabled={!canEdit} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} />
            </div>
          </div>
          <div className="field">
            <label>{lang === "es" ? "Color secundario" : "Secondary color"}</label>
            <div className="flex items-center gap-2">
              <input type="color" value={form.secondary_color} disabled={!canEdit} onChange={(e) => setForm({ ...form, secondary_color: e.target.value })} className="w-14 h-10 border border-line rounded cursor-pointer" data-testid="school-color-secondary" />
              <input className="input" value={form.secondary_color} disabled={!canEdit} onChange={(e) => setForm({ ...form, secondary_color: e.target.value })} />
            </div>
          </div>
        </div>
      </div>

      {/* Misión / Visión / Valores */}
      <div className="card space-y-3">
        <p className="lbl m-0">{lang === "es" ? "Misión, visión y valores" : "Mission, vision, values"}</p>
        <div className="field"><label>{lang === "es" ? "Misión" : "Mission"}</label>
          <textarea className="textarea" rows={2} value={form.mission} disabled={!canEdit} onChange={(e) => setForm({ ...form, mission: e.target.value })} data-testid="school-mission" /></div>
        <div className="field"><label>{lang === "es" ? "Visión" : "Vision"}</label>
          <textarea className="textarea" rows={2} value={form.vision} disabled={!canEdit} onChange={(e) => setForm({ ...form, vision: e.target.value })} data-testid="school-vision" /></div>
        <div className="field"><label>{lang === "es" ? "Valores" : "Values"}</label>
          <textarea className="textarea" rows={2} value={form.values} disabled={!canEdit} onChange={(e) => setForm({ ...form, values: e.target.value })} data-testid="school-values" /></div>
      </div>

      {canEdit && (
        <div className="flex items-center gap-3">
          <button className="btn" onClick={save} disabled={saving} data-testid="school-save-btn">
            <IconCheck size={13} /> {saving ? "..." : (lang === "es" ? "Guardar cambios" : "Save")}
          </button>
          {msg && <span className="text-[12.5px] text-tert">{msg}</span>}
        </div>
      )}
    </div>
  );
}

// ============================================================
// TAB: Equipo (staff management)
// ============================================================
function EquipoTab() {
  const { lang } = useI18n();
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", school_role: "docente", gender: "femenino" });
  const [saving, setSaving] = useState(false);
  const [lastCreated, setLastCreated] = useState(null);
  const [err, setErr] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/brujula/staff"); setStaff(data); } catch { /* */ }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setSaving(true); setErr(""); setLastCreated(null);
    try {
      const { data } = await api.post("/brujula/staff", form);
      setLastCreated(data);
      setForm({ email: "", name: "", school_role: "docente", gender: "femenino" });
      await load();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/brujula/staff/${deleteTarget.id}`);
      setDeleteTarget(null);
      await load();
    } catch (e) { setErr(formatApiError(e)); setDeleteTarget(null); }
  };

  return (
    <div className="space-y-5">
      <p className="text-muted text-[13.5px]">
        {lang === "es"
          ? "Cree cuentas para Orientadores/as y Docentes. La contraseña temporal se envía por correo. Docentes solo verán el panel de Adecuaciones; Orientadores/as tienen acceso completo salvo Equipo."
          : "Create Coordinator and Teacher accounts. Temp password is emailed. Teachers see only Accommodations."}
      </p>

      <div className="card space-y-3">
        <p className="lbl m-0">{lang === "es" ? "Agregar nuevo miembro" : "Add member"}</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="field"><label>{lang === "es" ? "Nombre completo" : "Full name"}</label>
            <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name" /></div>
          <div className="field"><label>{lang === "es" ? "Correo electrónico" : "Email"}</label>
            <input type="email" className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email" /></div>
          <div className="field"><label>{lang === "es" ? "Rol" : "Role"}</label>
            <select className="select-css" value={form.school_role} onChange={(e) => setForm({ ...form, school_role: e.target.value })} data-testid="staff-role">
              <option value="docente">Docente</option>
              <option value="orientador">Orientador/a</option>
            </select></div>
          <div className="field"><label>{lang === "es" ? "Género (para trato)" : "Gender"}</label>
            <select className="select-css" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} data-testid="staff-gender">
              <option value="femenino">Femenino</option>
              <option value="masculino">Masculino</option>
            </select></div>
        </div>
        <button className="btn" onClick={create} disabled={saving || !form.email || !form.name} data-testid="staff-create-btn">
          <IconMailForward size={13} /> {saving ? "..." : (lang === "es" ? "Crear y enviar acceso" : "Create & send")}
        </button>
        {err && <p className="text-errtext text-[13px]">{err}</p>}
        {lastCreated && (
          <div className="card border-l-[3px] border-l-teal" data-testid="staff-created-toast">
            <p className="text-[13px]"><IconCheck size={13} className="inline text-teal-deep" /> {lang === "es" ? "Cuenta creada. Se envió el correo de bienvenida con la contraseña temporal." : "Account created. Welcome email sent."}</p>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <p className="lbl">{lang === "es" ? "Miembros del equipo" : "Staff members"}</p>
        {staff.map((s) => (
          <div key={s.id} className="card flex items-center justify-between gap-3 flex-wrap" data-testid={`staff-row-${s.id}`}>
            <div>
              <p className="font-medium text-[14px]">{s.name}</p>
              <p className="text-tert text-[12px]">{s.email} · <span className="tag">{s.school_role || "director"}</span></p>
            </div>
            {s.school_role !== "director" && (
              <button
                className="btn-ghost btn text-[11px] px-2 py-1 text-errtext"
                onClick={() => setDeleteTarget(s)}
                data-testid={`staff-remove-${s.id}`}
              ><IconTrash size={12} /></button>
            )}
          </div>
        ))}
        {staff.length === 0 && <p className="text-tert text-[13px]">{lang === "es" ? "Sin miembros aún." : "No members yet."}</p>}
      </div>

      <ConfirmDeleteModal
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={remove}
        title={lang === "es" ? "Eliminar miembro del equipo" : "Remove staff"}
        description={deleteTarget ? `Se eliminará <b>${deleteTarget.name}</b> (${deleteTarget.email}). Esta acción es irreversible.` : ""}
        testId="brujula-staff-delete-modal"
      />
    </div>
  );
}

// ============================================================
// TAB: Índice de inclusión
// ============================================================
function IndiceInclusion() {
  const { lang } = useI18n();
  const [qs, setQs] = useState([]);
  const [ans, setAns] = useState({});
  const [result, setResult] = useState(null);
  const [hist, setHist] = useState([]);
  const load = async () => {
    const [{ data: q }, { data: h }] = await Promise.all([api.get("/brujula/inclusion/questions"), api.get("/brujula/inclusion/history")]);
    setQs(q.questions); setHist(h);
    const init = {}; q.questions.forEach((qq) => { init[qq.key] = 0; }); setAns(init);
  };
  useEffect(() => { load(); }, []);
  const apply = async () => {
    const { data } = await api.post("/brujula/inclusion/apply", { answers: ans });
    setResult(data); await load();
  };
  const downloadDocx = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/brujula/inclusion/docx?lang=${lang}`;
    window.open(url, "_blank");
  };
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-muted text-[13.5px]">
          {lang === "es" ? "Responda 6 preguntas sobre su centro. Obtenga un score orientador y compare con evaluaciones anteriores." : "Answer 6 questions about your school."}
        </p>
        <button className="btn-ghost btn text-[11.5px]" onClick={downloadDocx} data-testid="ii-docx"><IconDownload size={12} /> {lang === "es" ? "Descargar reporte (Word)" : "Download report"}</button>
      </div>
      {qs.map((q, i) => (
        <div key={q.key} className="card" data-testid={`ii-q-${q.key}`}>
          <p className="text-[14px] mb-3">{i + 1}. {q.q}</p>
          <div className="flex flex-wrap gap-2">
            {[0, 1, 2, 3, 4].map((v) => (
              <button key={v} className={`chip ${ans[q.key] === v ? "sel" : ""}`} onClick={() => setAns({ ...ans, [q.key]: v })} data-testid={`ii-a-${q.key}-${v}`}>
                {["Nada", "Poco", "Regular", "Bastante", "Totalmente"][v]}
              </button>
            ))}
          </div>
        </div>
      ))}
      <button className="btn" onClick={apply} data-testid="ii-submit"><IconCheck size={13} /> {lang === "es" ? "Calcular índice" : "Compute"}</button>
      {result && (
        <div className="card border-l-[3px] border-l-teal" data-testid="ii-result">
          <p className="eyebrow">{lang === "es" ? "Resultado" : "Result"}</p>
          <p className="font-display text-[28px] font-medium mt-1">{result.score}% · <span className="text-honey">{result.band}</span></p>
        </div>
      )}
      {hist.length > 0 && (
        <>
          <p className="lbl mt-6">{lang === "es" ? "Histórico" : "History"}</p>
          <div className="space-y-2">
            {hist.map((h) => (
              <div key={h.id} className="card flex items-center justify-between">
                <span className="text-[13px]">{new Date(h.created_at).toLocaleDateString(lang === "es" ? "es-HN" : "en-US")}</span>
                <span className="font-display text-[18px]">{h.score}% <span className="tag ml-2">{h.band}</span></span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================
// TAB: Simulacro
// ============================================================
function Simulacro() {
  const { lang } = useI18n();
  const [dims, setDims] = useState([]);
  const [ratings, setRatings] = useState({});
  const [notes, setNotes] = useState("");
  const [result, setResult] = useState(null);
  const [hist, setHist] = useState([]);
  const load = async () => {
    const [{ data: d }, { data: h }] = await Promise.all([api.get("/brujula/simulacro/dimensions"), api.get("/brujula/simulacro/history")]);
    setDims(d.dimensions); setHist(h);
    const r = {}; d.dimensions.forEach((x) => { r[x.key] = 3; }); setRatings(r);
  };
  useEffect(() => { load(); }, []);
  const run = async () => { const { data } = await api.post("/brujula/simulacro/apply", { ratings, notes }); setResult(data); await load(); };
  const downloadDocx = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/brujula/simulacro/docx?lang=${lang}`;
    window.open(url, "_blank");
  };
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-muted text-[13.5px]">
          {lang === "es" ? "Autoevalúe cada dimensión de 0 a 5 como si mañana fuera una visita de supervisión." : "Self-rate each dimension 0-5."}
        </p>
        <button className="btn-ghost btn text-[11.5px]" onClick={downloadDocx} data-testid="sim-docx"><IconDownload size={12} /> {lang === "es" ? "Descargar reporte (Word)" : "Download report"}</button>
      </div>
      {dims.map((d) => (
        <div key={d.key} className="card">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[14px] font-medium">{d.label}</p>
            <span className="text-tert text-[12px]">peso {d.weight}%</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {[0, 1, 2, 3, 4, 5].map((v) => (
              <button key={v} className={`chip ${ratings[d.key] === v ? "sel" : ""}`} onClick={() => setRatings({ ...ratings, [d.key]: v })} data-testid={`sim-${d.key}-${v}`}>{v}</button>
            ))}
          </div>
        </div>
      ))}
      <textarea className="textarea" placeholder={lang === "es" ? "Notas..." : "Notes..."} value={notes} onChange={(e) => setNotes(e.target.value)} />
      <button className="btn" onClick={run} data-testid="sim-run"><IconCheck size={13} /> {lang === "es" ? "Correr simulacro" : "Run mock"}</button>
      {result && (
        <div className="card border-l-[3px] border-l-teal" data-testid="sim-result">
          <p className="font-display text-[28px] font-medium">{result.score}% listos</p>
          <p className="text-honey text-[13px] mt-2">{lang === "es" ? "Área más débil:" : "Weakest:"} {result.weakest}</p>
        </div>
      )}
      {hist.length > 0 && (
        <>
          <p className="lbl mt-6">{lang === "es" ? "Histórico" : "History"}</p>
          {hist.map((h) => <div key={h.id} className="card flex items-center justify-between"><span className="text-[13px]">{new Date(h.created_at).toLocaleDateString()}</span><span className="font-display text-[18px]">{h.score}%</span></div>)}
        </>
      )}
    </div>
  );
}

// ============================================================
// TAB: Adecuaciones (con PDF parser + campos nuevos)
// ============================================================
function Adecuaciones({ schoolRole }) {
  const { lang } = useI18n();
  const { user } = useAuth();
  const [grades, setGrades] = useState([]);
  const [form, setForm] = useState({
    student_name: "",
    student_age: 8,
    grade_key: "",
    condition: "TDAH",
    area: "Lectura",
    context: "",
    teacher_name: user?.name || "",
    subject: "",
    plan_context: "",
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [planFileName, setPlanFileName] = useState("");

  useEffect(() => {
    (async () => { try { const { data } = await api.get("/brujula/grades"); setGrades(data.grades); } catch { /* */ } })();
  }, []);

  const uploadPlan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/brujula/adecuacion/parse-plan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPlanFileName(file.name);
      setForm((f) => ({
        ...f,
        teacher_name: data.teacher || f.teacher_name,
        subject: data.subject || f.subject,
        grade_key: data.grade_key || f.grade_key,
        plan_context: data.activities_summary || "",
      }));
    } catch (err) { setError(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const gen = async () => {
    setLoading(true); setError(""); setResult(null);
    try {
      const { data } = await api.post("/brujula/adecuacion/generate", { ...form, student_age: Number(form.student_age) });
      setResult(data);
    }
    catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const downloadDocx = () => {
    if (!result) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/brujula/adecuacion/plans/${result.id}/docx?lang=${lang}`;
    window.open(url, "_blank");
  };

  return (
    <div className="space-y-4">
      {schoolRole === "docente" && (
        <div className="card border-l-[3px] border-l-honey">
          <p className="text-[13px]">{lang === "es" ? "Los planes que genere quedan en estado pendiente hasta ser aprobados por el/la Orientador/a." : "Your plans go to 'pending' until Coordinator approves."}</p>
        </div>
      )}

      {/* PDF upload */}
      <div className="card">
        <p className="lbl m-0">{lang === "es" ? "1. (Opcional) Adjunte el plan de clase del docente" : "1. (Optional) Attach teacher's class plan"}</p>
        <p className="text-tert text-[12px] mt-1">{lang === "es" ? "Solo referencia — no se guarda. Nos ayuda a alinear las adecuaciones con las actividades reales." : "Reference only — not stored."}</p>
        <label className="btn btn-ghost text-[12px] mt-3 cursor-pointer inline-flex" data-testid="adec-upload-btn">
          <IconUpload size={13} /> {uploading ? "..." : planFileName || (lang === "es" ? "Subir PDF de plan semanal" : "Upload weekly plan PDF")}
          <input type="file" accept=".pdf,application/pdf" className="hidden" onChange={uploadPlan} data-testid="adec-upload-input" />
        </label>
        {planFileName && <span className="ml-2 text-teal-deep text-[12px]"><IconCheck size={12} className="inline" /> {planFileName}</span>}
      </div>

      {/* Formulario */}
      <div className="card">
        <p className="lbl m-0">{lang === "es" ? "2. Complete o revise los datos del estudiante" : "2. Fill student data"}</p>
        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          <div className="field"><label>{lang === "es" ? "Nombre del estudiante" : "Student name"}</label>
            <input className="input" value={form.student_name} onChange={(e) => setForm({ ...form, student_name: e.target.value })} data-testid="adec-name" /></div>
          <div className="field"><label>{lang === "es" ? "Edad" : "Age"}</label>
            <input type="number" className="input" value={form.student_age} onChange={(e) => setForm({ ...form, student_age: e.target.value })} data-testid="adec-age" /></div>
          <div className="field"><label>{lang === "es" ? "Grado" : "Grade"}</label>
            <select className="select-css" value={form.grade_key} onChange={(e) => setForm({ ...form, grade_key: e.target.value })} data-testid="adec-grade">
              <option value="">— {lang === "es" ? "Seleccione" : "Select"} —</option>
              {grades.map((g) => <option key={g.key} value={g.key}>{g[lang] || g.es}</option>)}
            </select></div>
          <div className="field"><label>{lang === "es" ? "Condición" : "Condition"}</label>
            <select className="select-css" value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} data-testid="adec-condition">
              <option>TDAH</option><option>TEA</option><option>Dislexia</option><option>Discalculia</option><option>Otro</option>
            </select></div>
          <div className="field"><label>{lang === "es" ? "Área" : "Area"}</label>
            <select className="select-css" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} data-testid="adec-area">
              <option>Lectura</option><option>Escritura</option><option>Matemáticas</option><option>Conducta y regulación</option><option>General</option>
            </select></div>
          <div className="field"><label>{lang === "es" ? "Nombre del docente" : "Teacher name"}</label>
            <input className="input" value={form.teacher_name} onChange={(e) => setForm({ ...form, teacher_name: e.target.value })} data-testid="adec-teacher" /></div>
          <div className="field"><label>{lang === "es" ? "Asignatura" : "Subject"}</label>
            <input className="input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder={lang === "es" ? "p. ej. Lectura, Ciencias..." : "e.g. Reading, Science..."} data-testid="adec-subject" /></div>
          <div className="field sm:col-span-2"><label>{lang === "es" ? "Contexto del estudiante" : "Student context"}</label>
            <textarea className="textarea" rows={2} value={form.context} onChange={(e) => setForm({ ...form, context: e.target.value })} data-testid="adec-context" /></div>
        </div>
        <button className="btn mt-3" onClick={gen} disabled={loading || !form.student_name} data-testid="adec-generate">
          <IconWand size={13} /> {loading ? "..." : (lang === "es" ? "Generar plan" : "Generate")}
        </button>
        {error && <p className="text-errtext text-[13px] mt-2">{error}</p>}
      </div>

      {result && (
        <div className="card border-l-[3px] border-l-teal" data-testid="adec-result">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <p className="eyebrow">{result.condition} · {result.area}</p>
              <p className="font-display text-[18px] font-medium mt-1">Plan para {result.student_name}</p>
              <p className="text-tert text-[12px] mt-0.5">{result.teacher_name || "—"} · {result.subject || "—"}</p>
            </div>
            <button className="btn-ghost btn text-[11.5px]" onClick={downloadDocx} data-testid="adec-docx-btn">
              <IconDownload size={12} /> {lang === "es" ? "Descargar Word (con logo)" : "Download Word"}
            </button>
          </div>
          <p className="lbl mt-4">Adecuaciones</p>
          <ul className="space-y-2 mt-2">
            {result.adecuaciones.map((a, i) => <li key={i} className="text-[13.5px] flex gap-2"><span className="text-honey">•</span> {a}</li>)}
          </ul>
          {result.evaluacion && <><p className="lbl mt-4">Evaluación</p><p className="text-[13.5px]">{result.evaluacion}</p></>}
          {result.seguimiento && <><p className="lbl mt-4">Seguimiento</p><p className="text-[13.5px]">{result.seguimiento}</p></>}
          <p className="text-tert text-[11.5px] mt-4">Plan guardado en Trazabilidad · aprobación: <span className="tag">{result.approval_status}</span></p>
        </div>
      )}
    </div>
  );
}

// ============================================================
// TAB: Trazabilidad (con filtros + approval)
// ============================================================
function Trazabilidad({ schoolRole }) {
  const { lang } = useI18n();
  const [plans, setPlans] = useState([]);
  const [grades, setGrades] = useState([]);
  const [filters, setFilters] = useState({ grade_key: "", teacher: "", approval_status: "" });
  const [reviewOpen, setReviewOpen] = useState(null);
  const [reviewNote, setReviewNote] = useState("");
  const canApprove = schoolRole === "director" || schoolRole === "orientador";

  const load = async () => {
    const params = new URLSearchParams();
    if (filters.grade_key) params.set("grade_key", filters.grade_key);
    if (filters.teacher) params.set("teacher", filters.teacher);
    if (filters.approval_status) params.set("approval_status", filters.approval_status);
    const { data } = await api.get(`/brujula/adecuacion/plans?${params.toString()}`);
    setPlans(data);
  };
  useEffect(() => {
    (async () => { try { const { data } = await api.get("/brujula/grades"); setGrades(data.grades); } catch { /* */ } })();
  }, []);
  useEffect(() => { load(); }, [filters]);

  const setStatus = async (id, status) => { await api.patch(`/brujula/adecuacion/plans/${id}`, { status }); await load(); };

  const submitReview = async (planId, action) => {
    await api.post(`/brujula/adecuacion/plans/${planId}/approval`, { action, note: reviewNote });
    setReviewOpen(null); setReviewNote(""); await load();
  };

  const downloadDocx = (planId) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/brujula/adecuacion/plans/${planId}/docx?lang=${lang}`;
    window.open(url, "_blank");
  };

  const gradeLabel = (key) => grades.find((g) => g.key === key)?.[lang] || grades.find((g) => g.key === key)?.es || "—";

  const approvalBadge = (s) => {
    if (s === "approved") return { label: lang === "es" ? "aprobado" : "approved", cls: "bg-okbg text-oktext" };
    if (s === "pending") return { label: lang === "es" ? "pendiente" : "pending", cls: "bg-honey-soft text-honey-deep" };
    if (s === "changes_requested") return { label: lang === "es" ? "cambios solicitados" : "changes requested", cls: "bg-errbg text-errtext" };
    return { label: s || "—", cls: "" };
  };

  return (
    <div className="space-y-3">
      <p className="text-muted text-[13.5px]">
        {lang === "es" ? "Cada plan generado queda registrado. Aquí controle su cumplimiento y aprobación." : "Every plan is tracked."}
      </p>

      {/* Filtros */}
      <div className="card">
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="field"><label>{lang === "es" ? "Filtrar por grado" : "Filter by grade"}</label>
            <select className="select-css" value={filters.grade_key} onChange={(e) => setFilters({ ...filters, grade_key: e.target.value })} data-testid="tra-filter-grade">
              <option value="">{lang === "es" ? "Todos" : "All"}</option>
              {grades.map((g) => <option key={g.key} value={g.key}>{g[lang] || g.es}</option>)}
            </select></div>
          <div className="field"><label>{lang === "es" ? "Filtrar por docente" : "Filter by teacher"}</label>
            <input className="input" value={filters.teacher} onChange={(e) => setFilters({ ...filters, teacher: e.target.value })} placeholder={lang === "es" ? "Nombre..." : "Name..."} data-testid="tra-filter-teacher" /></div>
          <div className="field"><label>{lang === "es" ? "Estado de aprobación" : "Approval"}</label>
            <select className="select-css" value={filters.approval_status} onChange={(e) => setFilters({ ...filters, approval_status: e.target.value })} data-testid="tra-filter-approval">
              <option value="">{lang === "es" ? "Todos" : "All"}</option>
              <option value="pending">{lang === "es" ? "Pendientes" : "Pending"}</option>
              <option value="approved">{lang === "es" ? "Aprobados" : "Approved"}</option>
              <option value="changes_requested">{lang === "es" ? "Cambios solicitados" : "Changes requested"}</option>
            </select></div>
        </div>
      </div>

      {plans.map((p) => {
        const app = approvalBadge(p.approval_status);
        return (
          <div key={p.id} className="card" data-testid={`plan-${p.id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <p className="font-display text-[16px] font-medium">{p.student_name} · {p.condition} · {p.area}</p>
                <p className="text-tert text-[12px]">
                  {gradeLabel(p.grade_key) || p.grade || "—"} · {lang === "es" ? "docente" : "teacher"}: {p.teacher_name || "—"} · {p.subject || "—"} · creado {new Date(p.created_at).toLocaleDateString(lang === "es" ? "es-HN" : "en-US")}
                </p>
                {p.reviewed_by_name && (
                  <p className="text-tert text-[11.5px] mt-0.5">
                    {lang === "es" ? "Revisado por" : "Reviewed by"} {p.reviewed_by_name} · {new Date(p.reviewed_at).toLocaleDateString()}
                    {p.review_note && <span className="ml-2 italic">&quot;{p.review_note}&quot;</span>}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`tag ${app.cls}`} data-testid={`plan-approval-${p.id}`}>{app.label}</span>
                <span className={`tag ${p.status === "active" ? "bg-teal-soft text-teal-deep" : p.status === "fulfilled" ? "bg-okbg text-oktext" : ""}`}>{p.status}</span>
                <button className="btn-ghost btn text-[11px]" onClick={() => downloadDocx(p.id)} data-testid={`plan-docx-${p.id}`}><IconDownload size={11} /></button>
                {canApprove && p.approval_status === "pending" && (
                  <button className="btn-ghost btn text-[11px]" onClick={() => { setReviewOpen(p.id); setReviewNote(""); }} data-testid={`plan-review-${p.id}`}>
                    <IconClipboardCheck size={11} /> {lang === "es" ? "Revisar" : "Review"}
                  </button>
                )}
                {p.status === "active" && canApprove && (
                  <>
                    <button className="btn-ghost btn text-[11px]" onClick={() => setStatus(p.id, "fulfilled")} data-testid={`plan-fulfill-${p.id}`}><IconCheck size={11} /> {lang === "es" ? "Cumplido" : "Fulfilled"}</button>
                    <button className="btn-ghost btn text-[11px]" onClick={() => setStatus(p.id, "archived")} data-testid={`plan-archive-${p.id}`}><IconArchive size={11} /></button>
                  </>
                )}
              </div>
            </div>

            {reviewOpen === p.id && (
              <div className="mt-3 pt-3 border-t border-line">
                <textarea className="textarea" rows={2} placeholder={lang === "es" ? "Nota de revisión (opcional)..." : "Review note (optional)..."} value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} data-testid={`plan-review-note-${p.id}`} />
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <button className="btn text-[11.5px]" onClick={() => submitReview(p.id, "approve")} data-testid={`plan-approve-${p.id}`}><IconCheck size={11} /> {lang === "es" ? "Aprobar" : "Approve"}</button>
                  <button className="btn-ghost btn text-[11.5px]" onClick={() => submitReview(p.id, "request_changes")} data-testid={`plan-changes-${p.id}`}><IconAlertTriangle size={11} /> {lang === "es" ? "Solicitar cambios" : "Request changes"}</button>
                  <button className="btn-ghost btn text-[11px] text-tert" onClick={() => setReviewOpen(null)}><IconX size={11} /></button>
                </div>
              </div>
            )}
          </div>
        );
      })}
      {plans.length === 0 && <p className="text-tert text-[13px]">{lang === "es" ? "Sin planes aún." : "No plans yet."}</p>}
    </div>
  );
}

// ============================================================
// TAB: Defensa
// ============================================================
function Defensa() {
  const { lang } = useI18n();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", kind: "protocolo", content: "" });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const load = async () => { const { data } = await api.get("/brujula/defense"); setItems(data); };
  useEffect(() => { load(); }, []);
  const add = async () => { if (!form.title) return; await api.post("/brujula/defense", form); setForm({ title: "", kind: "protocolo", content: "" }); await load(); };
  const downloadDocx = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/brujula/defense/docx?lang=${lang}`;
    window.open(url, "_blank");
  };
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-muted text-[13.5px]">
          {lang === "es" ? "Todo lo que sostiene la práctica inclusiva de su escuela: protocolos, evidencias, comunicaciones." : "Everything backing your school's inclusive practice."}
        </p>
        <button className="btn-ghost btn text-[11.5px]" onClick={downloadDocx} data-testid="def-docx"><IconDownload size={12} /> {lang === "es" ? "Descargar carpeta (Word)" : "Download folder"}</button>
      </div>
      <div className="card">
        <div className="grid sm:grid-cols-[2fr_1fr] gap-2">
          <input className="input" placeholder={lang === "es" ? "Título" : "Title"} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="def-title" />
          <select className="select-css" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="def-kind">
            <option value="protocolo">protocolo</option><option value="evidencia">evidencia</option><option value="comunicacion">comunicación</option><option value="otro">otro</option>
          </select>
        </div>
        <textarea className="textarea mt-2" placeholder={lang === "es" ? "Contenido..." : "Content..."} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
        <button className="btn mt-2" onClick={add} data-testid="def-add"><IconPlus size={13} /> {lang === "es" ? "Agregar" : "Add"}</button>
      </div>
      <div className="space-y-2">
        {items.map((d) => (
          <div key={d.id} className="card flex items-center justify-between" data-testid={`def-${d.id}`}>
            <div>
              <span className="tag">{d.kind}</span>
              <p className="font-medium text-[14px] mt-1">{d.title}</p>
              {d.content && <p className="text-tert text-[12px] mt-1 line-clamp-2">{d.content}</p>}
            </div>
            <button
              className="btn-ghost btn text-[11px] px-2 py-1 text-errtext"
              onClick={() => setDeleteTarget(d)}
              data-testid={`def-remove-${d.id}`}
              title={lang === "es" ? "Eliminar documento" : "Delete document"}
            >
              <IconTrash size={12} />
            </button>
          </div>
        ))}
        {items.length === 0 && <p className="text-tert text-[13px]">{lang === "es" ? "Sin documentos." : "No docs."}</p>}
      </div>
      <ConfirmDeleteModal
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={async () => {
          await api.delete(`/brujula/defense/${deleteTarget.id}`);
          setDeleteTarget(null);
          await load();
        }}
        title={lang === "es" ? "Eliminar documento de defensa" : "Delete defense document"}
        description={
          deleteTarget
            ? (lang === "es"
                ? `Se eliminará <b>${deleteTarget.title}</b> (${deleteTarget.kind}). Esta acción es irreversible.`
                : `<b>${deleteTarget.title}</b> (${deleteTarget.kind}) will be removed. This is irreversible.`)
            : ""
        }
        testId="brujula-def-delete-modal"
      />
    </div>
  );
}
